import React, { useState, useEffect } from 'react';
import { apiCall } from '../utils/api';
import { toast } from './ui/sonner';
import { 
  MessageSquare, Search, X, ChevronLeft, Send, 
  User, Clock, Circle, Loader2, Inbox, Users, Check, CheckCheck
} from 'lucide-react';
import MessagingPanel from './MessagingPanel';
import { AttachmentPicker, AttachmentChips, AttachmentList, VoiceRecorderButton } from './MessageAttachments';
import { ReactionControl } from './MessageReactions';
import { useTypingChannel, TypingDots } from './TypingIndicator';
import { useWebSocket } from '../contexts/WebSocketContext';

export default function MessagingInbox({ onClose, initialRecipient = null }) {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [view, setView] = useState(initialRecipient ? 'chat' : 'list');

  useEffect(() => {
    loadConversations();
    if (initialRecipient) {
      setSelectedConversation(initialRecipient);
      setView('chat');
    }
  }, [initialRecipient]);

  const loadConversations = async () => {
    setLoading(true);
    try {
      const response = await apiCall('/api/messages/conversations');
      setConversations(response.conversations || []);
    } catch (error) {
      console.error('Failed to load conversations:', error);
      // Initialize with empty array if endpoint doesn't exist yet
      setConversations([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectConversation = (conv) => {
    setSelectedConversation({
      id: conv.other_user_id,
      name: conv.other_user_name,
      role: conv.other_user_role,
      jobId: conv.job_id
    });
    setView('chat');
  };

  const handleBack = () => {
    setSelectedConversation(null);
    setView('list');
    loadConversations();
  };

  const filteredConversations = conversations.filter(conv =>
    conv.other_user_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const currentUserId = JSON.parse(localStorage.getItem('user'))?._id;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="messaging-inbox">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl h-[600px] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-cyan-600 p-4 text-white flex items-center justify-between">
          <div className="flex items-center gap-3">
            {view === 'chat' && (
              <button
                onClick={handleBack}
                className="p-2 hover:bg-white/20 rounded-lg transition"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
            )}
            <div className="p-2 bg-white/20 backdrop-blur-sm rounded-xl">
              <MessageSquare className="w-6 h-6" />
            </div>
            <div>
              <h2 className="font-bold text-lg">
                {view === 'chat' ? selectedConversation?.name : 'Messages'}
              </h2>
              <p className="text-white/80 text-sm">
                {view === 'chat' 
                  ? selectedConversation?.role || 'User'
                  : `${conversations.length} conversation${conversations.length !== 1 ? 's' : ''}`
                }
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/20 rounded-lg transition"
            data-testid="close-inbox"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {view === 'list' ? (
          <>
            {/* Search */}
            <div className="p-4 border-b border-gray-200">
              <div className="relative">
                <Search className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search conversations..."
                  className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-300 rounded-xl text-slate-900 placeholder-slate-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                  data-testid="search-conversations"
                />
              </div>
            </div>

            {/* Conversations List */}
            <div className="flex-1 overflow-y-auto">
              {loading ? (
                <div className="flex items-center justify-center h-full">
                  <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
                </div>
              ) : filteredConversations.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-gray-500 p-8">
                  <div className="p-4 bg-gray-100 rounded-full mb-4">
                    <Inbox className="w-12 h-12 text-gray-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-700 mb-2">No Messages Yet</h3>
                  <p className="text-center text-sm">
                    Start a conversation with an employer or worker from a job listing
                  </p>
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {filteredConversations.map((conv, idx) => (
                    <button
                      key={idx}
                      onClick={() => handleSelectConversation(conv)}
                      className="w-full p-4 hover:bg-gray-50 transition flex items-start gap-3 text-left"
                      data-testid="conversation-item"
                    >
                      {/* Avatar */}
                      <div className="relative">
                        <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-full flex items-center justify-center text-white font-semibold">
                          {conv.other_user_name?.charAt(0).toUpperCase() || 'U'}
                        </div>
                        {conv.is_online && (
                          <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-white"></div>
                        )}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <h4 className="font-semibold text-gray-900 truncate">
                            {conv.other_user_name}
                          </h4>
                          <span className="text-xs text-gray-500">
                            {conv.last_message_time 
                              ? new Date(conv.last_message_time).toLocaleDateString([], {
                                  month: 'short', day: 'numeric'
                                })
                              : ''
                            }
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 truncate">
                          {conv.last_message || 'No messages yet'}
                        </p>
                        {conv.unread_count > 0 && (
                          <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 bg-blue-600 text-white text-xs font-bold rounded-full mt-1">
                            {conv.unread_count}
                          </span>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </>
        ) : (
          /* Chat View - Embedded MessagingPanel */
          <div className="flex-1 flex flex-col">
            <EmbeddedChat 
              recipientId={selectedConversation?.id}
              recipientName={selectedConversation?.name}
              jobId={selectedConversation?.jobId}
            />
          </div>
        )}
      </div>
    </div>
  );
}

// Embedded chat component (similar to MessagingPanel but embedded in modal)
function EmbeddedChat({ recipientId, recipientName, jobId }) {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const messagesEndRef = React.useRef(null);
  const { lastMessage } = useWebSocket();
  const debounceRef = React.useRef(null);

  useEffect(() => {
    loadMessages();
    const interval = setInterval(loadMessages, 30000);  // safety-net poll (30s)
    return () => {
      clearInterval(interval);
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [recipientId]);

  useEffect(() => {
    if (!lastMessage) return;
    if (lastMessage.type === 'messages_read' && String(lastMessage.reader_id || '') === String(recipientId)) {
      setMessages((prev) => prev.map((x) => x.sender_id !== recipientId ? { ...x, read: true } : x));
      return;
    }
    if (lastMessage.type !== 'new_message') return;
    const involvesPeer = (
      String(lastMessage.sender_id || '') === String(recipientId) ||
      String(lastMessage.data?.sender_id || '') === String(recipientId) ||
      String(lastMessage.data?.recipient_id || '') === String(recipientId)
    );
    if (!involvesPeer) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(loadMessages, 300);
  }, [lastMessage, recipientId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadMessages = async () => {
    if (!recipientId) return;
    setLoading(true);
    try {
      const response = await apiCall(`/api/messages/${recipientId}`);
      setMessages(response.messages || []);
    } catch (error) {
      console.error('Failed to load messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() && attachments.length === 0) return;

    const sentAttachments = attachments;
    setSending(true);
    try {
      await apiCall('/api/messages', {
        method: 'POST',
        body: JSON.stringify({
          recipient_id: recipientId,
          job_id: jobId,
          message: newMessage,
          attachments: sentAttachments,
        }),
      });
      setNewMessage('');
      setAttachments([]);
      emitStopTyping();
      loadMessages();
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const currentUserId = JSON.parse(localStorage.getItem('user'))?._id;
  const { otherTyping, emitTyping, emitStopTyping } = useTypingChannel(recipientId);

  return (
    <>
      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50">
        {loading && messages.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="w-6 h-6 text-blue-600 animate-spin" />
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-gray-500">
            <MessageSquare className="w-12 h-12 text-gray-300 mb-3" />
            <p className="text-sm">No messages yet. Start the conversation!</p>
          </div>
        ) : (
          messages.map((msg, idx) => {
            const isSent = msg.sender_id === currentUserId;
            return (
              <div
                key={idx}
                className={`flex ${isSent ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[75%] rounded-2xl px-4 py-2.5 ${
                    isSent
                      ? 'bg-gradient-to-r from-blue-600 to-cyan-600 text-white'
                      : 'bg-white text-gray-900 border border-gray-200'
                  }`}
                >
                  {msg.message && <p className="text-sm">{msg.message}</p>}
                  <AttachmentList attachments={msg.attachments} mine={isSent} />
                  <ReactionControl
                    msgId={msg.msg_id || msg._id}
                    reactions={msg.reactions}
                    currentUserId={currentUserId}
                    mine={isSent}
                    onChanged={(id, reactions) => setMessages((prev) => prev.map((x) => (x.msg_id === id || x._id === id) ? { ...x, reactions } : x))}
                  />
                  <p className={`text-xs mt-1 flex items-center gap-1 ${
                    isSent ? 'text-blue-100' : 'text-gray-500'
                  }`}>
                    {new Date(msg.created_at).toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                    {isSent && (
                      msg.read
                        ? <span className="inline-flex items-center gap-0.5" data-testid={`seen-tick-${msg.msg_id || msg._id}`}><CheckCheck className="w-3 h-3" /> Seen</span>
                        : <Check className="w-3 h-3 opacity-70" />
                    )}
                  </p>
                </div>
              </div>
            );
          })
        )}
        {otherTyping && <TypingDots />}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <form onSubmit={handleSendMessage} className="p-4 border-t border-gray-200 bg-white">
        <AttachmentChips attachments={attachments} onRemove={(i) => setAttachments((prev) => prev.filter((_, idx) => idx !== i))} />
        <div className="flex gap-2">
          <AttachmentPicker attachments={attachments} setAttachments={setAttachments} disabled={sending} />
          <VoiceRecorderButton attachments={attachments} setAttachments={setAttachments} disabled={sending} />
          <input
            type="text"
            value={newMessage}
            onChange={(e) => { setNewMessage(e.target.value); emitTyping(); }}
            placeholder="Type a message..."
            className="flex-1 px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            disabled={sending}
            data-testid="message-input"
          />
          <button
            type="submit"
            disabled={sending || (!newMessage.trim() && attachments.length === 0)}
            className="px-5 py-3 bg-gradient-to-r from-blue-600 to-cyan-600 text-white rounded-xl hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition flex items-center gap-2"
            data-testid="send-message-button"
          >
            {sending ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Send className="w-5 h-5" />
            )}
          </button>
        </div>
      </form>
    </>
  );
}
